/**
 * EventQueue — shared batched analytics delivery (CDP Phase 1, FLO-547).
 *
 * Extracted from the FLO-367 nudge-telemetry machinery (onboarding-sdk.ts) so
 * the nudge beacon and the new visitor-event stream share ONE proven delivery
 * path instead of two divergent copies:
 *
 *   - bounded queue (default 200; oldest dropped first — analytics, not ledger)
 *   - 3s debounce so a burst of events rides one request
 *   - drains in batches of ≤50 (the server's batch cap) until empty, so a
 *     queue larger than one batch is fully delivered — critical on the final
 *     page-hide flush, which has no follow-up
 *   - page-hide/visibility-hidden flush via a keepalive fetch, which survives
 *     unload (NOT navigator.sendBeacon — see {@link sendEventBatch})
 *   - a keepalive BYTE budget, because `keepalive: true` buys unload survival
 *     at the cost of a hard quota — see {@link KEEPALIVE_AGGREGATE_BYTES}
 *
 * Nothing here throws into the host page: analytics is best-effort by design.
 * The transport is injectable so batching/debounce/cap logic is unit-testable
 * in a plain node environment (client/ has no jsdom — see vitest.config.ts).
 */

import { logger as defaultLogger, Logger } from './logger';

/**
 * The Fetch standard's keepalive quota: 64 KiB across ALL in-flight keepalive
 * request BODIES at once — not per request, and not per EventQueue. A fetch
 * that would take the total past it rejects its promise, and our transport
 * swallows that rejection (analytics must not throw into the host page), so an
 * over-budget batch would disappear silently. The counter is therefore
 * module-level: the SDK runs two queues (nudge telemetry + visitor events)
 * that both flush on the SAME page-hide, and the browser bills them together.
 *
 * This is the one thing `navigator.sendBeacon` did NOT do better — it has the
 * same 64 KB quota, but a refused beacon returns false synchronously, so the
 * old code could fall back to a plain fetch. A keepalive fetch gives no such
 * signal, which is why the budget has to be enforced before sending.
 */
export const KEEPALIVE_AGGREGATE_BYTES = 64 * 1024;

/**
 * Ceiling for a SINGLE body. Deliberately under the aggregate quota: the
 * quota is charged on more than the JSON we measure (the browser counts the
 * whole request), and one batch must never be able to consume the entire
 * page's keepalive allowance on its own.
 */
export const MAX_KEEPALIVE_BODY_BYTES = 60 * 1024;

/** Bytes handed to a transport that has not yet reported completion. */
let inFlightKeepaliveBytes = 0;

const utf8Encoder = typeof TextEncoder !== 'undefined' ? new TextEncoder() : null;

/**
 * Encoded size of a body, in the units the keepalive quota is counted in.
 *
 * `String.length` is UTF-16 code units and undercounts every non-ASCII
 * character — and undercounting is precisely how a body sails past the quota
 * and is dropped. The hand-rolled fallback exists only for a UA with no
 * TextEncoder, which necessarily also has no keepalive fetch; it is exact
 * rather than approximate so the budget never lies.
 */
export function bodyByteLength(body: string): number {
  if (utf8Encoder) return utf8Encoder.encode(body).length;
  let bytes = 0;
  for (const char of body) {
    const cp = char.codePointAt(0) ?? 0;
    bytes += cp < 0x80 ? 1 : cp < 0x800 ? 2 : cp < 0x10000 ? 3 : 4;
  }
  return bytes;
}

/** Claim `bytes` of the page's keepalive allowance; false = no room right now. */
function reserveKeepaliveBytes(bytes: number, limit: number): boolean {
  if (inFlightKeepaliveBytes + bytes > limit) return false;
  inFlightKeepaliveBytes += bytes;
  return true;
}

function releaseKeepaliveBytes(bytes: number): void {
  inFlightKeepaliveBytes = Math.max(0, inFlightKeepaliveBytes - bytes);
}

/**
 * TEST ONLY. The in-flight counter is module state by design (see
 * {@link KEEPALIVE_AGGREGATE_BYTES}); a test whose transport never settles
 * would otherwise leak its reservation into the next test.
 */
export function __resetKeepaliveBudgetForTests(): void {
  inFlightKeepaliveBytes = 0;
}

function isThenable(value: unknown): value is PromiseLike<unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { then?: unknown }).then === 'function'
  );
}

export interface EventQueueOptions<T> {
  /** Resolved per flush, not per construction — config can settle later. */
  url: () => string;
  /** Wrap a ≤batchSize slice in the endpoint's body shape (clientKey etc.). */
  buildBody: (events: T[]) => Record<string, unknown>;
  /**
   * Injectable transport (tests). Defaults to {@link sendEventBatch}.
   * `useBeacon` marks the FINAL (page-hide / dispose) flush — the default
   * transport is keepalive either way; the flag exists so a transport that
   * must read a response knows it can't on this one, and so the queue knows
   * a withheld batch has no later flush to ride.
   *
   * Declared `void`, but a transport MAY return a promise: the queue then
   * holds that batch's keepalive reservation until it settles (true in-flight
   * accounting). A transport that returns nothing has its reservation
   * released when the flush ends, making the budget a per-flush cap.
   */
  send?: (url: string, body: string, useBeacon: boolean) => void;
  /** Per-body ceiling override (tests/tuning). Default 60 KiB. */
  maxBodyBytes?: number;
  /**
   * Aggregate in-flight keepalive ceiling override (tests/tuning). Default
   * 64 KiB. The COUNTER is module-wide whatever this says — the browser bills
   * every queue on the page together.
   */
  keepaliveBudgetBytes?: number;
  /** Oldest events are dropped past this. Default 200. */
  maxQueue?: number;
  /** Server-side batch cap. Default 50. */
  batchSize?: number;
  /** Debounce before a non-beacon flush. Default 3000. */
  debounceMs?: number;
  logger?: Logger;
}

/**
 * The default transport: ONE shape for every flush — a credential-free
 * keepalive fetch, which survives unload the same way a beacon does.
 *
 * It is deliberately NOT navigator.sendBeacon. A beacon's request has
 * credentials mode "include" (Beacon spec, non-negotiable), and an
 * application/json Blob body is not a CORS-safelisted content type, so the
 * beacon preflights AS A CREDENTIALED request. Our public SDK endpoints are
 * anonymous (clientKey + Origin in the application layer) and therefore reply
 * `credentials: false` — no `Access-Control-Allow-Credentials` header — so the
 * browser rejected every page-hide batch on floe.so with:
 *
 *   "The value of the 'Access-Control-Allow-Credentials' header in the
 *    response is '' which must be 'true' when the request's credentials mode
 *    is 'include'"
 *
 * The old `if (!sent) fetch(...)` fallback could not catch that: sendBeacon
 * returns true once the payload is QUEUED, and the CORS failure happens later
 * and asynchronously — so the batch was lost AND a red error was printed on
 * the customer's page. Widening CORS was never the fix: nothing here reads a
 * cookie, and the SDK runs on customer domains, so `credentials: 'omit'` is
 * both the correct posture and the reason no preflight can fail this way
 * again. The preflight itself is cached for a day (cors maxAge=86400) and the
 * debounced flushes warm it long before unload.
 *
 * Returns a promise that settles when the request does — swallowed either
 * way, but {@link EventQueue} uses it to release that batch's share of the
 * keepalive quota ({@link KEEPALIVE_AGGREGATE_BYTES}). The declared option
 * type is still `void`; nothing is obliged to return anything.
 */
export function sendEventBatch(url: string, body: string, _useBeacon: boolean): Promise<void> {
  try {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
      // Anonymous endpoint: never attach cookies. Explicit rather than relying
      // on the 'same-origin' default — the dashboard dogfoods this SDK, where
      // the API can BE same-origin and the default would start sending them.
      credentials: 'omit',
    }).then(
      () => undefined,
      () => undefined,
    );
  } catch {
    /* analytics must never throw into the host page */
    return Promise.resolve();
  }
}

/** One serialized, budget-measured batch, ready to hand to a transport. */
interface FramedBatch<T> {
  events: T[];
  body: string;
  bytes: number;
}

export class EventQueue<T> {
  private readonly options: Required<Pick<EventQueueOptions<T>, 'maxQueue' | 'batchSize' | 'debounceMs'>> &
    EventQueueOptions<T>;
  private readonly logger: Logger;
  private queue: T[] = [];
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private unloadBound = false;
  // Retained so dispose() can remove them — otherwise a torn-down owner stays
  // pinned to window/document and a pending timer can flush after teardown.
  private flushHandler: (() => void) | null = null;
  private visibilityHandler: (() => void) | null = null;

  constructor(options: EventQueueOptions<T>) {
    this.options = {
      maxQueue: 200,
      batchSize: 50,
      debounceMs: 3000,
      ...options,
    };
    this.logger = options.logger ?? defaultLogger;
  }

  /** Queue depth — exposed for tests and debugging only. */
  get size(): number {
    return this.queue.length;
  }

  /** Queue an event; flushed on the debounce and on page-hide (keepalive). */
  enqueue(event: T): void {
    if (this.queue.length >= this.options.maxQueue) this.queue.shift();
    this.queue.push(event);
    this.bindUnloadFlush();
    this.scheduleFlush();
  }

  /**
   * Queue `event` in place of a still-unsent one that `same` matches, or at
   * the end when there is none. For an event that SUPERSEDES its earlier self
   * (a page visit's summary, re-sent with more in it): two copies in one batch
   * would have the server keep the older.
   */
  replaceOrEnqueue(event: T, same: (queued: T) => boolean): void {
    const at = this.queue.findIndex((queued) => same(queued));
    if (at === -1) {
      this.enqueue(event);
      return;
    }
    this.queue[at] = event;
    this.scheduleFlush();
  }

  /** Arm the debounce, unless one is already pending. */
  private scheduleFlush(): void {
    if (this.flushTimer) return;
    this.flushTimer = setTimeout(() => {
      this.flushTimer = null;
      this.flush(false);
    }, this.options.debounceMs);
  }

  /**
   * Send queued events in successive batches of ≤batchSize, within the page's
   * keepalive byte budget. The queue shrinks every iteration (splice), so this
   * always terminates even if a send throws.
   *
   * `isFinal` (page-hide / dispose) is what decides the fate of anything the
   * budget will not take: on an ordinary flush it stays QUEUED for the next
   * one, on the final flush it is dropped, because there is no next one.
   */
  flush(isFinal: boolean): void {
    const send = this.options.send ?? sendEventBatch;
    const budget = this.options.keepaliveBudgetBytes ?? KEEPALIVE_AGGREGATE_BYTES;
    // Reservations held by transports that returned nothing — released when
    // this flush ends, since we have no completion signal for them.
    let unreportedBytes = 0;
    let withheld = false;
    try {
      while (this.queue.length > 0) {
        let framed: FramedBatch<T> | null;
        let url: string;
        try {
          // frameBatch() FIRST: it removes its events from the queue before
          // anything can throw, so a permanently-throwing url()/buildBody()
          // drains the queue instead of spinning this loop forever.
          framed = this.frameBatch();
          url = this.options.url();
        } catch (e) {
          // Neither may throw into the host page. The batch is lost exactly as
          // it was before this budget existed — forward progress is the
          // invariant that matters here.
          this.logger.warn('[EventQueue] flush failed for a batch:', e);
          continue;
        }
        // The head event alone exceeded the body ceiling and was dropped.
        if (!framed) continue;

        if (!reserveKeepaliveBytes(framed.bytes, budget)) {
          if (isFinal) {
            // No later flush and no later page. Drop deliberately and loudly
            // (debug-gated) rather than pretending it was delivered.
            this.logger.warn(
              `[EventQueue] final flush over the ${budget}B keepalive budget — dropping ${framed.events.length + this.queue.length} event(s)`,
            );
            this.queue = [];
          } else {
            // Put the batch back, in order, and try again on a later flush.
            this.queue.unshift(...framed.events);
            withheld = true;
          }
          break;
        }

        unreportedBytes += framed.bytes;
        let settled: unknown;
        try {
          settled = send(url, framed.body, isFinal);
        } catch (e) {
          this.logger.warn('[EventQueue] flush failed for a batch:', e);
        }
        if (isThenable(settled)) {
          // A transport that reports completion gets true in-flight
          // accounting: its bytes stay reserved until the request settles.
          unreportedBytes -= framed.bytes;
          const bytes = framed.bytes;
          settled.then(
            () => releaseKeepaliveBytes(bytes),
            () => releaseKeepaliveBytes(bytes),
          );
        }
      }
    } finally {
      releaseKeepaliveBytes(unreportedBytes);
    }
    // A withheld batch has no timer behind it (the debounce that ran this
    // flush already cleared itself), so arm one — otherwise a quiet page
    // never retries and the events wait for page-hide to be dropped.
    if (withheld) this.scheduleFlush();
  }

  /**
   * Take the next batch and serialize it, SPLITTING until the body fits the
   * per-body ceiling.
   *
   * Returns null when the head event alone is too large to ever ride a
   * keepalive request: it is dropped (with a debug log) rather than left to
   * block the queue forever. Events are removed from the queue before
   * serializing, so a throwing buildBody() cannot spin this loop.
   */
  private frameBatch(): FramedBatch<T> | null {
    const limit = this.options.maxBodyBytes ?? MAX_KEEPALIVE_BODY_BYTES;
    const taken = this.queue.splice(0, this.options.batchSize);
    let count = taken.length;
    for (;;) {
      const events = taken.slice(0, count);
      const body = JSON.stringify(this.options.buildBody(events));
      const bytes = bodyByteLength(body);
      if (bytes <= limit || count === 1) {
        // Whatever we did not take goes back at the FRONT, order intact.
        if (count < taken.length) this.queue.unshift(...taken.slice(count));
        if (bytes > limit) {
          this.logger.warn(
            `[EventQueue] dropping one ${bytes}B event: a single event cannot exceed the ${limit}B keepalive body ceiling`,
          );
          return null;
        }
        return { events, body, bytes };
      }
      count = Math.floor(count / 2);
    }
  }

  /** Drop everything queued WITHOUT sending (the consent kill switch). */
  clear(): void {
    this.queue = [];
  }

  /**
   * Final (page-hide-shaped) flush + full unbind. After this the queue is
   * inert but reusable: a later enqueue re-binds the unload listeners.
   */
  dispose(): void {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    this.flush(true);
    if (this.flushHandler && typeof window !== 'undefined') {
      window.removeEventListener('pagehide', this.flushHandler);
    }
    if (this.visibilityHandler && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
    }
    this.flushHandler = null;
    this.visibilityHandler = null;
    this.unloadBound = false;
  }

  /** Lazily bind the page-hide flush on first use (mirrors FLO-367). */
  private bindUnloadFlush(): void {
    if (this.unloadBound || typeof window === 'undefined') return;
    this.unloadBound = true;
    this.flushHandler = () => this.flush(true);
    this.visibilityHandler = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        this.flushHandler?.();
      }
    };
    window.addEventListener('pagehide', this.flushHandler);
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.visibilityHandler);
    }
  }
}
